/**
 * FAAB that changed hands in a trade, as money that has actually left a balance.
 *
 * Tim, 9 September: "i had FAAB in trades that should have been deducted/added as necessary once
 * completed. I made manual adjustments instead."
 *
 * He is describing a hole the app had carried all season. FAAB is a tradeable asset — the compose
 * form offers it, `settlementFor` crosses it over with everything else, and `Trade.sides[i].faab`
 * records what each man received. But `tradeSettle.ts` wrote four things and none of them was a
 * ledger row: the archive said ₣10 went from Tim to John and every balance in the app disagreed.
 * The `'trade'` kind existed in `FaabEntry` from the beginning and nothing had ever written one.
 *
 * MODELLED ON `luxuryCharge.ts`, which solved the same problem for the keeper tax, and deliberately
 * so — one pattern for "a figure the rules derive, reconciled against an append-only ledger":
 *
 *   - What the trades SAY is computed from the archive, never stored as a balance.
 *   - What the ledger HOLDS is summed from its `trade` rows, ignoring reversed ones.
 *   - Only the difference is offered as a write, so a second run after a good first returns nothing
 *     and the button that calls it has nothing to say.
 *   - The id is derived, so a double-press computes the same id twice and the second create is
 *     REFUSED by the rules rather than moving the money again.
 *
 * WHY IT IS ALSO A RECONCILER AND NOT ONLY A HOOK ON SETTLEMENT. The obvious fix is two rows
 * written the moment a trade settles, and that is here — `settlePlan` carries them now. But every
 * trade already settled this season settled without them, which is precisely Tim's complaint, and a
 * hook on settlement cannot reach backwards. So the same function answers both questions, and the
 * FAAB page can show him where the archive and the ledger disagree on any trade, old or new.
 *
 * AND IT WILL NOT QUIETLY UNDO HIS MANUAL ADJUSTMENTS. The corrections he made by hand are
 * `adjustment` rows, not `trade` rows, and this counts only its own kind — so a trade he squared by
 * hand still reads as unposted here. That is the honest answer rather than the convenient one: this
 * cannot tell a hand-made correction for THIS trade from any other adjustment, and guessing would
 * either double a man's money or hide a real gap. The page names both figures and lets him reverse
 * the manual row, which is a thing he can now do in one press.
 */

import { managerShort } from './identity.js';
import type { FaabEntry, GmId, Trade } from '../store/types.js';
import { isVoid, voidedIds } from './faabVoid.js';

/** Every FAAB row written for a trade is this kind, and nothing else writes it. */
export const TRADE_KIND = 'trade' as const;

/** `trade-live-abc-TIM`, and `-2` for a correction after a reversal. See the note above. */
export function tradeFaabId(tradeId: string, gm: string, seq: number): string {
  const base = `trade-${tradeId}-${gm.toUpperCase()}`;
  return seq <= 1 ? base : `${base}-${seq}`;
}

/** Is this row one of the trade's own, rather than some other trade's or somebody's adjustment? */
const belongsTo = (e: FaabEntry, tradeId: string, gm: string): boolean => {
  const base = `trade-${tradeId}-${gm.toUpperCase()}`;
  return e.id === base || e.id.startsWith(`${base}-`);
};

export interface TradeMove {
  tradeId: string;
  gm: GmId;
  /** The other man in the deal, for the description. */
  other: GmId;
  /** What the trade says moved: negative if he sent it, positive if he got it. */
  says: number;
  /** What the ledger has posted for this trade and this man, reversals excluded. */
  posted: number;
  /** The write that would square them. Never zero in a returned row. */
  delta: number;
  /** Which row of his for this trade, 1-based. The id is built from it. */
  seq: number;
  /** The trade's date, so the ledger row lands on the day the deal did. */
  date: string;
}

/**
 * What a trade says each side's FAAB did.
 *
 * `sides[i].faab` is what side i RECEIVED — `settlementFor` crosses the sides over on the way into
 * the archive, which is the one thing in that function worth reading twice. So a man's movement is
 * what he got minus what the other man got, and a two-way FAAB swap nets rather than posting four
 * rows that cancel in pairs.
 */
export function tradeFaabSays(t: Trade): Array<{ gm: GmId; other: GmId; amount: number }> {
  const out: Array<{ gm: GmId; other: GmId; amount: number }> = [];
  for (const i of [0, 1] as const) {
    const mine = t.sides[i];
    const theirs = t.sides[i === 0 ? 1 : 0];
    const amount = Math.round((mine.faab ?? 0) - (theirs.faab ?? 0));
    if (amount === 0) continue;
    out.push({
      gm: mine.gm.toUpperCase() as GmId,
      other: theirs.gm.toUpperCase() as GmId,
      amount,
    });
  }
  return out;
}

/**
 * Where the archive and the ledger disagree, trade by trade and man by man.
 *
 * A trade appears only where they differ, so a fully posted season returns an empty array. Trades
 * from other seasons are skipped rather than reconciled: the ledger is per-season and a 2019 deal's
 * FAAB was spent in 2019.
 */
export function tradeFaabGaps(
  trades: readonly Trade[],
  entries: readonly FaabEntry[],
  season: number,
): TradeMove[] {
  const voided = voidedIds(entries);
  const out: TradeMove[] = [];
  for (const t of trades) {
    if (t.season !== season) continue;
    for (const { gm, other, amount } of tradeFaabSays(t)) {
      const mine = entries.filter((e) => e.kind === TRADE_KIND && belongsTo(e, t.id, gm));
      const posted = mine
        .filter((e) => !isVoid(e) && !voided.has(e.id))
        .reduce((n, e) => n + e.amount, 0);
      if (posted === amount) continue;
      out.push({
        tradeId: t.id,
        gm,
        other,
        says: amount,
        posted,
        delta: amount - posted,
        seq: mine.length + 1,
        date: t.date,
      });
    }
  }
  return out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0)
    || a.gm.localeCompare(b.gm));
}

/**
 * The gaps as ledger entries.
 *
 * The amount written is the DELTA, for the reason the tax rows are deltas: the ledger appends, so
 * squaring a man who is ₣4 short of what the trade says means a row of ₣4 and not a row of ₣10 that
 * pays him twice.
 *
 * The DATE IS THE TRADE'S, not today's. The FAAB moved when the deal did, and the ledger is grouped
 * by day and read as a chronology — filing an August trade under September would make the balance
 * right and the history wrong, which is the trade the whole append-only rule refuses to make.
 */
export function tradeFaabEntries(
  moves: readonly TradeMove[],
  season: number,
  by: GmId,
  _at: string,
): FaabEntry[] {
  return moves.map((m) => ({
    id: tradeFaabId(m.tradeId, m.gm, m.seq),
    season,
    gm: m.gm,
    amount: m.delta,
    kind: TRADE_KIND,
    description: m.posted === 0
      ? `${m.says < 0 ? 'FAAB sent to' : 'FAAB from'} ${managerShort(m.other)} in trade`
      : `Trade FAAB with ${managerShort(m.other)} — corrected to `
        + `${m.says < 0 ? '−' : '+'}₣${Math.abs(m.says)}`,
    date: m.date.slice(0, 10),
    recordedBy: by,
  }));
}

/** The figure on the button: what a run moves, and how many rows it writes. */
export function tradeFaabSummary(moves: readonly TradeMove[]): { rows: number; gross: number } {
  return {
    rows: moves.length,
    gross: moves.reduce((n, m) => n + Math.abs(m.delta), 0),
  };
}
